import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CreatePatientPayload,
  CreatePatientResponse,
  FetchPatientListResponse,
  FetchPatientsQueryParam,
  PatientByIdResponse,
  SearchPatientResponse,
  SimplePatient,
  SimplePatientsResponse,
  StatusMessageResponse,
  StudiesDocumentsPayload,
  StudiesResponse,
} from '@shared/models';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  private _http = inject(HttpClient);
  private _apiUrl = environment.apiUrl;
  private jsonUrl = 'assets/mock-data/patient.json';
  private _authHeaders = new HttpHeaders({
    Authorization: '',
  });

  private patients: SimplePatientsResponse[] = [];

  public fetchPatients(
    queryParams?: Partial<FetchPatientsQueryParam>
  ): Observable<FetchPatientListResponse> {
    let params = new HttpParams();
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }
    return this._http.get<FetchPatientListResponse>(
      `${this._apiUrl}/api/patient/list`,
      {
        headers: this._authHeaders,
        params,
      }
    );
  }

  public fetchMockPatients(): Observable<SimplePatient[]> {
    return this._http.get<SimplePatient[]>(this.jsonUrl, {
      headers: this._authHeaders,
    });
  }

  public searchPatient(formValue: {
    name: string;
    birthday: Date | null;
    lastname: string;
  }): Observable<SearchPatientResponse> {
    const birthday = formValue.birthday ?? new Date();
    const year = birthday.getFullYear();
    const month = birthday.getMonth() + 1;
    const day = birthday.getDate();

    const birthdateParsed = `${year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;

    return this._http.get<SearchPatientResponse>(
      `${this._apiUrl}/api/patient/search?firstname=${formValue.name}&lastname=${formValue.lastname}&birthdate=${birthdateParsed}`,
      { headers: this._authHeaders }
    );
  }

  public createPatient(formValue: CreatePatientResponse) {
    const payload: CreatePatientPayload = {
      firstname: formValue.firstname,
      lastname: formValue.lastname,
      tutor_name: formValue.tutor_name || null,
      gender: formValue.gender,
      birthdate: formValue.birthdate,
      pathologies: formValue.pathologies,
      address: formValue.address || '',
      email: formValue.email || '',
      health_insurance_id: formValue.health_insurance_id || null,
      prepaid_id: formValue.prepaid_id || null,
      id_card: formValue.id_card,
      id_number: formValue.id_number,
      phone_number: formValue.phone_number || '',
      medic_id: formValue.medic_id,
    };

    return this._http.post(`${this._apiUrl}/api/patient/register`, payload, {
      headers: this._authHeaders,
    });
  }

  public editPatient(id: number, formValue: CreatePatientPayload) {
    return this._http.patch(`${this._apiUrl}/api/patient/${id}`, formValue, {
      headers: this._authHeaders,
    });
  }

  public linkPatient(id: number): Observable<StatusMessageResponse> {
    const payload = {
      id,
    };

    return this._http.post<StatusMessageResponse>(
      `${this._apiUrl}/api/patient/filter/register`,
      payload,
      { headers: this._authHeaders }
    );
  }

  public deletePatient(id: number): Observable<StatusMessageResponse> {
    return this._http.delete<StatusMessageResponse>(
      `${this._apiUrl}/api/patient/${id}`,
      { headers: this._authHeaders }
    );
  }

  fetchPatientsSimple(id?: number): Observable<SimplePatient[]> {
    let params = new HttpParams();

    if (id !== undefined) {
      params = params.set('medic_id', id.toString());
    }

    return this._http
      .get<SimplePatientsResponse>(`${this._apiUrl}/api/patient/simple-list`, {
        headers: this._authHeaders,
        params: params,
      })
      .pipe(map((resp) => resp.patient_list));
  }

  fetchPatientById(id: number): Observable<PatientByIdResponse> {
    return this._http.get<PatientByIdResponse>(
      `${this._apiUrl}/api/patient/profile/${id}`,
      {
        headers: this._authHeaders,
      }
    );
  }

  fetchDocumentByPatientId(
    id: number,
    perPage?: number,
    page?: number
  ): Observable<StudiesResponse> {
    let params = new HttpParams();
    if (perPage) {
      params = params.set('per_page', perPage.toString());
    }
    if (page) {
      params = params.set('page', page.toString());
    }
    return this._http.get<StudiesResponse>(
      `${this._apiUrl}/api/patient/${id}/studies-documents`,
      {
        headers: this._authHeaders,
        params,
      }
    );
  }

  createNewStudiesDocument(id: number, formData: StudiesDocumentsPayload) {
    const form = new FormData();

    form.append('file_name', formData.fileName);
    if (formData.file) {
      form.append('file', formData.file);
    }
    form.append('observations', formData.observations);

    return this._http.post(
      `${this._apiUrl}/api/patient/${id}/store/studies-documents`,
      form,
      { headers: this._authHeaders }
    );
  }

  deleteStudyDocument(
    patientId: number,
    studyId: number
  ): Observable<StatusMessageResponse> {
    const url = `${this._apiUrl}/api/patient/${patientId}/studies-documents`;
    const payload = {
      study_id: studyId,
    };

    return this._http.delete<StatusMessageResponse>(url, {
      headers: this._authHeaders,
      body: payload,
    });
  }

  editStudyDocument(id: number, formData: StudiesDocumentsPayload) {

    let form = {
      study_id: formData.studyId,
      file_name: formData.fileName,
      observations: formData.observations,
      ...(formData.file && { file: formData.file })
    };
    
    return this._http.patch(
      `${this._apiUrl}/api/patient/${id}/studies-documents`,
      form,
      { headers: this._authHeaders }
    );
  }
}
